import { GeofenceGroup } from '@/common/models';
import ErrorHandler from '@/ErrorHandler';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import API from '@/org-app/api';
import { createGetAllAction } from '@/utils/ActionsUtils';
import UserNotifications from '@/utils/UserNotifications';
import i18n from '@/vueI18n';

import * as types from './mutation-types';

const actions = {
  createNewGeofenceGroup({ commit }, type) {
    const geofenceGroup = GeofenceGroup.fromObject({ name: '', description: '', usageID: type });
    commit(types.CREATE_GEOFENCE_GROUP, geofenceGroup);
    return geofenceGroup;
  },

  deleteGeofenceGroups({ commit }, geofenceGroups) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      let successfullApiCalls = 0;
      let failedApiCalls = 0;

      const evalCompletion = () => {
        if (successfullApiCalls + failedApiCalls === geofenceGroups.length) {
          if (successfullApiCalls === geofenceGroups.length) {
            resolve(true);
            GlobalEventBus.$emit(GlobalEvents.loaded);
          } else {
            reject();
            const msg = i18n.global.t('messages.failedToDeleteGeofenceGroups');
            UserNotifications.error({ message: `${msg}` });
            GlobalEventBus.$emit(GlobalEvents.loaded);
          }
        }
      };

      geofenceGroups.forEach((geofenceGroup) => {
        API.instance.deleteGeofenceGroup(
          geofenceGroup.id,
          () => {
            commit(types.DELETE_GEOFENCE_GROUP, geofenceGroup);
            successfullApiCalls += 1;
            evalCompletion();
          },
          () => {
            failedApiCalls += 1;
            evalCompletion();
          },
        );
      });
    });
  },

  deleteGeofenceGroup({ commit }, geofenceGroup) {
    return new Promise((resolve, reject) => {
      GlobalEventBus.$emit(GlobalEvents.loading);

      API.instance.deleteGeofenceGroup(
        geofenceGroup.id,
        () => {
          commit(types.DELETE_GEOFENCE_GROUP, geofenceGroup);
          resolve(true);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToDeleteGeofenceGroup');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  getGeofenceGroups: createGetAllAction(
    API.instance.getGeofenceGroups,
    types.UPDATE_GEOFENCE_GROUPS_LIST,
    'messages.failedToGetGeofenceGroups',
    true,
  ),

  getPublicPlanGeofenceGroups: createGetAllAction(
    API.instance.getPublicPlanGeofenceGroups,
    types.UPDATE_GEOFENCE_GROUPS_LIST,
    'messages.failedToGetGeofenceGroups',
    true,
  ),

  async getGeofenceGroup({ commit }, { id, cancelTokenSource = null, showLoader = true, loadGeofences = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const result = await API.instance.getGeofenceGroup({ id, cancelTokenSource, loadGeofences }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetGeofenceUnitMapping');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return result;
  },

  async getGeofenceUnitMapping({ commit }, { geofenceGroupID, cancelTokenSource = null, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const result = await API.instance.getGeofenceUnitMapping({ geofenceGroupID, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetGeofenceUnitMapping');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return result;
  },

  async getGeofenceSiteMappingForSites(
    { commit },
    { geofenceGroupID, siteIDs, cancelTokenSource = null, showLoader = true },
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const result = await API.instance
      .getGeofenceSiteMappingForSitesPOST({ geofenceGroupID, siteIDs, cancelTokenSource })
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToGetGeofenceSiteMappingForSites');
        ErrorHandler.showError(msg, err);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return result;
  },

  async getGeofenceUnitMappingForUnits(
    { commit },
    { geofenceGroupID, unitIDs, cancelTokenSource = null, showLoader = true },
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const result = await API.instance
      .getGeofenceUnitMappingForUnitsPOST({ geofenceGroupID, unitIDs, cancelTokenSource })
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToGetGeofenceUnitMappingForUnits');
        ErrorHandler.showError(msg, err);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return result;
  },

  async getGeofenceSiteMapping({ commit }, { geofenceGroupID, cancelTokenSource = null, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const result = await API.instance.getGeofenceSiteMapping({ geofenceGroupID, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetGeofenceSiteMapping');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return result;
  },

  async previewGeofenceGroup(
    { commit },
    {
      geofenceGroup,
      excludeInactive = true,
      excludeUnavailable = true,
      useSaved = false,
      buyFromDealsEnabled = false,
      cancelTokenSource = null,
      showLoader = true,
    },
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const result = await API.instance
      .previewGeofenceGroup({
        geofenceGroup,
        excludeInactive,
        excludeUnavailable,
        useSaved,
        buyFromDealsEnabled,
        cancelTokenSource,
      })
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToPreviewGeofenceGroup');
        ErrorHandler.showError(msg, err);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return result;
  },

  saveGeofenceGroup({ commit }, { geofenceGroup, showLoader = true }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    return new Promise((resolve, reject) => {
      const successCallback = (data) => {
        commit(types.SAVE_GEOFENCE_GROUP, data);
        resolve(data);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
      };

      const errorCallback = (error) => {
        const msg = i18n.global.t('messages.failedToSaveGeofenceGroup');
        ErrorHandler.showError(msg, error);
        reject(error);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
      };

      if (geofenceGroup.id === -1) {
        API.instance.postGeofenceGroup(geofenceGroup, successCallback, errorCallback);
      } else {
        API.instance.putGeofenceGroup(geofenceGroup, successCallback, errorCallback);
      }
    });
  },

  saveGeofences({ commit }, geofenceGroup) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      const successCallback = (data) => {
        resolve(data);
        GlobalEventBus.$emit(GlobalEvents.loaded);
      };

      const errorCallback = (error) => {
        const msg = i18n.global.t('messages.failedToSaveGeofences');
        ErrorHandler.showError(msg, error);
        reject(error);
        GlobalEventBus.$emit(GlobalEvents.loaded);
      };

      const originalGeofences = geofenceGroup.originalObject != null ? geofenceGroup.originalObject.geofences : [];
      const toAdd = geofenceGroup.geofences.filter((x) => x.isNew);
      const toUpdate = geofenceGroup.geofences.filter(
        (x) =>
          originalGeofences.filter((y) => !x.isNew && y.id === x.id && !y.timestamp.equals(x.timestamp)).length > 0,
      );
      const toDelete = originalGeofences.filter(
        (x) => geofenceGroup.geofences.filter((y) => y.id === x.id).length === 0,
      );

      if (toAdd.length + toUpdate.length + toDelete.length > 50) {
        // Breakdown the items in smaller batches.
        const batchSize = 50;
        let numberOfBatchesToSend =
          Math.ceil(toAdd.length / batchSize) +
          Math.ceil(toUpdate.length / batchSize) +
          Math.ceil(toDelete.length / batchSize);
        const checkForBatchCompletion = () => {
          numberOfBatchesToSend -= 1;
          if (numberOfBatchesToSend === 0) {
            successCallback();
          }
        };
        for (let i = 0; i < Math.ceil(toAdd.length / batchSize); i += 1) {
          API.instance.patchGeofences(
            toAdd.slice(i * batchSize, (i + 1) * batchSize),
            [],
            [],
            checkForBatchCompletion,
            errorCallback,
          );
        }
        for (let i = 0; i < Math.ceil(toUpdate.length / batchSize); i += 1) {
          API.instance.patchGeofences(
            [],
            toUpdate.slice(i * batchSize, (i + 1) * batchSize),
            [],
            checkForBatchCompletion,
            errorCallback,
          );
        }
        for (let i = 0; i < Math.ceil(toDelete.length / batchSize); i += 1) {
          API.instance.patchGeofences(
            [],
            [],
            toDelete.slice(i * batchSize, (i + 1) * batchSize),
            checkForBatchCompletion,
            errorCallback,
          );
        }
      } else {
        API.instance.patchGeofences(toAdd, toUpdate, toDelete, successCallback, errorCallback);
      }
    });
  },

  uploadGeofenceGroupFile({ commit }, file) {
    GlobalEventBus.$emit(GlobalEvents.loading);
    return new Promise((resolve, reject) => {
      API.instance.uploadGeofenceGroupFile(
        file,
        (data) => {
          resolve(data);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
        (error) => {
          const msg = i18n.global.t('messages.failedToSaveGeofenceGroup');
          ErrorHandler.showError(msg, error);
          reject(error);
          GlobalEventBus.$emit(GlobalEvents.loaded);
        },
      );
    });
  },

  async getGenericGeofenceGroupTypeCountries(
    { commit },
    { queryParams, cancelTokenSource = null, showLoader = false },
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const geofenceGroups = await API.instance
      .getGenericGeofenceGroupTypeCountries({ queryParams, cancelTokenSource })
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToGetGenericGeofences');
        ErrorHandler.showError(msg, err);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });

    let geofenceGroupItems = geofenceGroups.items;

    if (queryParams?.filterCollection?.filters?.find((x) => x.property === 'country_code')?.values.length === 0) {
      geofenceGroupItems = [];
    }

    commit(types.SET_GENERIC_GEOFENCE_TYPES_COUNTRIES, geofenceGroupItems);

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return geofenceGroups;
  },

  async getGenericGeofences(__, { queryParams, cancelTokenSource = null, showLoader = false }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const geofences = await API.instance.getGenericGeofences({ queryParams, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetGenericGeofences');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return geofences;
  },

  async getGenericGeofencesWithinRadius(__, { radius, lat, lng, cancelTokenSource = null, showLoader = false }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const geofences = await API.instance
      .getGenericGeofencesWithinRadius({ radius, lat, lng, cancelTokenSource })
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToGetGenericGeofences');
        ErrorHandler.showError(msg, err);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }

    return geofences;
  },

  getGeofences: createGetAllAction(API.instance.getGeofences, null, 'messages.failedToGetGeofences'),
};

export default actions;
